import { forwardRef } from 'react';
import { cn } from '../utils/cn'; // 假设有一个工具函数来合并className

/**
 * 通用按钮组件
 * 
 * 统一圆角样式规范：
 * - primary: rounded-3xl (12px) - 主要操作按钮
 * - secondary: rounded-2xl (8px) - 次要操作按钮  
 * - ghost: rounded-3xl (12px) - 幽灵按钮
 * - danger: rounded-2xl (8px) - 危险操作按钮
 */
export const Button = forwardRef(function Button(
  {
    variant = 'primary',
    size = 'medium',
    children,
    className,
    as: Component = 'button',
    ...props
  },
  ref
) {
  const baseClasses = 'inline-flex items-center justify-center font-semibold transition-all duration-200 cursor-pointer border focus:outline-none focus:ring-2 focus:ring-accent/50 disabled:cursor-not-allowed disabled:opacity-60';
  
  const variants = {
    primary: 'primary-pill rounded-3xl border border-sky-300/60 text-[#02111c] shadow-[0_10px_30px_rgba(14,165,233,0.35)] hover:shadow-[0_15px_40px_rgba(14,165,233,0.45)]',
    secondary: 'bg-slate-800/80 border border-slate-600/60 text-white rounded-2xl hover:bg-slate-700/80 hover:border-slate-500',
    ghost: 'ghost-pill rounded-3xl border border-slate-600/60 text-text hover:border-accent hover:text-sky-100',
    danger: 'bg-red-500/20 border border-red-500/50 text-red-200 rounded-2xl hover:bg-red-500/30 hover:border-red-400',
    custom: '' // 自定义样式，由className提供
  };

  const disabledVariants = {
    primary: 'bg-slate-800/70 border border-slate-600/60 text-slate-300 rounded-3xl shadow-none hover:shadow-none',
    secondary: 'bg-slate-800/60 border border-slate-700/60 text-slate-400 rounded-2xl',
    ghost: 'bg-slate-900/30 border border-slate-700/50 text-slate-500 rounded-3xl',
    danger: 'bg-red-500/10 border border-red-500/30 text-red-200/70 rounded-2xl',
    custom: '',
  };

  const sizes = {
    small: 'px-3 py-1.5 text-xs gap-1.5',
    medium: 'px-4 md:px-5 py-2 text-sm gap-2',
    large: 'px-6 py-3 text-base gap-3',
    icon: 'w-9 h-9'
  };

  const isDisabled = Boolean(props.disabled);
  const variantClass = isDisabled ? (disabledVariants[variant] || variants[variant]) : variants[variant];

  return (
    <Component
      ref={ref}
      className={cn(
        baseClasses,
        variantClass,
        sizes[size],
        className
      )}
      {...props}
    >
      {children}
    </Component>
  );
});

Button.displayName = 'Button';

/**
 * 特殊用途的按钮组件
 */
export const TelegramLoginButton = ({ children, ...props }) => (
  <Button
    variant="custom"
    className="w-full bg-[#0088cc] hover:bg-[#0088cc]/90 text-white rounded-2xl shadow-lg hover:shadow-xl"
    size="large"
    {...props}
  >
    {children}
  </Button>
);
